"use client"

import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import { cn } from "@/shared/lib/utils"

type HoverDropdownMenuContextValue = {
    openNow: () => void
    scheduleClose: () => void
}

const HoverDropdownMenuContext = React.createContext<HoverDropdownMenuContextValue | null>(null)

function useHoverDropdownMenuContext() {
    const context = React.useContext(HoverDropdownMenuContext)
    if (!context) {
        throw new Error("HoverDropdownMenu components must be used within HoverDropdownMenu")
    }
    return context
}

const HoverDropdownMenu = ({
    children,
    closeDelayMs = 100,
    defaultOpen,
    modal = false,
    open: openProp,
    onOpenChange,
    ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> & {
    closeDelayMs?: number
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false)
    const closeTimerRef = React.useRef<number | null>(null)
    const open = openProp ?? uncontrolledOpen

    const setOpen = React.useCallback((nextOpen: boolean) => {
        if (openProp === undefined) {
            setUncontrolledOpen(nextOpen)
        }
        onOpenChange?.(nextOpen)
    }, [onOpenChange, openProp])

    const clearCloseTimer = React.useCallback(() => {
        if (closeTimerRef.current === null) return
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
    }, [])

    const openNow = React.useCallback(() => {
        clearCloseTimer()
        setOpen(true)
    }, [clearCloseTimer, setOpen])

    const scheduleClose = React.useCallback(() => {
        clearCloseTimer()
        closeTimerRef.current = window.setTimeout(() => {
            setOpen(false)
            closeTimerRef.current = null
        }, closeDelayMs)
    }, [clearCloseTimer, closeDelayMs, setOpen])

    React.useEffect(() => {
        return () => {
            clearCloseTimer()
        }
    }, [clearCloseTimer])

    return (
        <HoverDropdownMenuContext.Provider value={{ openNow, scheduleClose }}>
            <DropdownMenu modal={modal} open={open} onOpenChange={setOpen} {...props}>
                {children}
            </DropdownMenu>
        </HoverDropdownMenuContext.Provider>
    )
}

const HoverDropdownMenuTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ onKeyDown, onPointerEnter, onPointerLeave, ...props }, ref) => {
    const { openNow, scheduleClose } = useHoverDropdownMenuContext()

    return (
        <DropdownMenuTrigger
            ref={ref}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
                    openNow()
                }
                onKeyDown?.(event)
            }}
            onPointerEnter={(event) => {
                openNow()
                onPointerEnter?.(event)
            }}
            onPointerLeave={(event) => {
                scheduleClose()
                onPointerLeave?.(event)
            }}
            {...props}
        />
    )
})
HoverDropdownMenuTrigger.displayName = "HoverDropdownMenuTrigger"

const HoverDropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ onCloseAutoFocus, onPointerEnter, onPointerLeave, ...props }, ref) => {
    const { openNow, scheduleClose } = useHoverDropdownMenuContext()

    return (
        <DropdownMenuContent
            ref={ref}
            onCloseAutoFocus={(event) => {
                event.preventDefault()
                onCloseAutoFocus?.(event)
            }}
            onPointerEnter={(event) => {
                openNow()
                onPointerEnter?.(event)
            }}
            onPointerLeave={(event) => {
                scheduleClose()
                onPointerLeave?.(event)
            }}
            {...props}
        />
    )
})
HoverDropdownMenuContent.displayName = "HoverDropdownMenuContent"

const HoverDropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => {
    return (
        <DropdownMenuItem
            ref={ref}
            className={cn(
                "transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                className,
            )}
            {...props}
        />
    )
})
HoverDropdownMenuItem.displayName = "HoverDropdownMenuItem"

export {
    HoverDropdownMenu,
    HoverDropdownMenuContent,
    HoverDropdownMenuItem,
    HoverDropdownMenuTrigger,
}